import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions as useCommissions } from '@/hooks/useCommission'
import { useMyWithdrawals as useWithdrawals, useRequestWithdrawal } from '@/hooks/useWithdrawal'

export const InstructorEarningsPage: React.FC = () => {
    const { user } = useAuth()
    const { data: commissions } = useCommissions(user?.id || '')
    const { data: withdrawals } = useWithdrawals(user?.id || '')
    const requestWithdrawal = useRequestWithdrawal()
    const [showWithdrawModal, setShowWithdrawModal] = useState(false)
    const [withdrawAmount, setWithdrawAmount] = useState('')
    const [withdrawBankName, setWithdrawBankName] = useState('')
    const [withdrawAccountNumber, setWithdrawAccountNumber] = useState('')
    const [withdrawAccountName, setWithdrawAccountName] = useState('')
    const [withdrawError, setWithdrawError] = useState<string | null>(null)

    const totalEarned = commissions?.filter((c: any) => c.status === 'paid')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const pendingEarnings = commissions?.filter((c: any) => c.status === 'pending')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const availableBalance = commissions?.filter((c: any) => c.status === 'earned')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0

    const minWithdrawal = 100000

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/20 text-yellow-400'
            case 'earned': return 'bg-blue-500 text-white'
            case 'paid': return 'bg-emerald-500 text-white'
            default: return 'bg-slate-400 text-white'
        }
    }

    const handleWithdrawSubmit = () => {
        setWithdrawError(null)

        if (!user?.id) {
            setWithdrawError('User not authenticated')
            return
        }

        const amountNum = parseInt(withdrawAmount, 10)
        if (isNaN(amountNum) || amountNum < minWithdrawal) {
            setWithdrawError(`Minimum withdrawal amount is Rp ${minWithdrawal.toLocaleString('id-ID')}`)
            return
        }

        if (!withdrawBankName || !withdrawAccountNumber || !withdrawAccountName) {
            setWithdrawError('Please fill in all bank details')
            return
        }

        requestWithdrawal.mutate({
            user_id: user.id,
            amount: amountNum,
            bank_name: withdrawBankName,
            account_number: withdrawAccountNumber,
            account_name: withdrawAccountName,
        }, {
            onSuccess: () => {
                setWithdrawAmount('')
                setWithdrawBankName('')
                setWithdrawAccountNumber('')
                setWithdrawAccountName('')
                setShowWithdrawModal(false)
            },
            onError: (error: any) => {
                setWithdrawError(error?.response?.data?.error?.message || 'Failed to submit withdrawal request')
            }
        })
    }

    const resetWithdrawModal = () => {
        setWithdrawAmount('')
        setWithdrawBankName('')
        setWithdrawAccountNumber('')
        setWithdrawAccountName('')
        setWithdrawError(null)
        setShowWithdrawModal(false)
    }

    return (
        <div>
            <PageHeader
                title="Earnings"
                subtitle="Track your commissions and request withdrawals"
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 p-5 sm:p-8 relative overflow-hidden shadow-sm group">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                        <span className="material-symbols-outlined text-[100px] text-emerald-500">account_balance_wallet</span>
                    </div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="size-10 rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
                                <span className="material-symbols-outlined text-[20px]">account_balance_wallet</span>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-xs font-black tracking-[0.2em]">Saldo Tersedia</p>
                        </div>
                        <p className="text-emerald-500 text-2xl sm:text-4xl font-black tracking-tight leading-none mb-6">Rp {availableBalance.toLocaleString('id-ID')}</p>
                        <button
                            onClick={() => setShowWithdrawModal(true)}
                            className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.25rem] font-black text-sm hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none"
                        >
                            Tarik Saldo
                        </button>
                    </div>
                </div>
                
                <div className="rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 p-5 sm:p-8 relative overflow-hidden shadow-sm group">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                        <span className="material-symbols-outlined text-[100px] text-amber-500">schedule</span>
                    </div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="size-10 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                                <span className="material-symbols-outlined text-[20px]">schedule</span>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-xs font-black tracking-[0.2em]">Pending (30 Hari)</p>
                        </div>
                        <p className="text-slate-900 dark:text-white text-2xl sm:text-4xl font-black tracking-tight leading-none mb-4">Rp {pendingEarnings.toLocaleString('id-ID')}</p>
                        <p className="text-[11px] font-bold text-slate-400 tracking-widest uppercase">Terkunci hingga masa refund berakhir</p>
                    </div>
                </div>

                <div className="rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 p-5 sm:p-8 relative overflow-hidden shadow-sm group">
                    <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                        <span className="material-symbols-outlined text-[100px] text-blue-500">trending_up</span>
                    </div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="size-10 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                                <span className="material-symbols-outlined text-[20px]">trending_up</span>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-xs font-black tracking-[0.2em]">Total Pendapatan</p>
                        </div>
                        <p className="text-slate-900 dark:text-white text-2xl sm:text-4xl font-black tracking-tight leading-none mb-4">Rp {totalEarned.toLocaleString('id-ID')}</p>
                        <p className="text-[11px] font-bold text-slate-400 tracking-widest uppercase">Akumulasi seluruh waktu</p>
                    </div>
                </div>
            </div>

            {/* Commission History */}
            <div className="rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 overflow-hidden mb-10 shadow-sm">
                <div className="px-8 py-6 border-b border-slate-50 dark:border-white/5 flex items-center gap-4">
                    <div className="size-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                        <span className="material-symbols-outlined text-[24px]">history_edu</span>
                    </div>
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Riwayat Komisi</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50/50 dark:bg-white/[0.01] border-b border-slate-100 dark:border-white/5">
                                <th className="text-left px-4 sm:px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">KURSUS</th>
                                <th className="text-left px-4 sm:px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">NOMINAL</th>
                                <th className="text-left px-4 sm:px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">STATUS</th>
                                <th className="text-left px-4 sm:px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">TANGGAL</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-white/5">
                            {commissions?.slice(0, 10).map((c: any) => (
                                <tr key={c.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-colors">
                                    <td className="px-4 sm:px-8 py-4 sm:py-5 text-sm font-bold text-slate-900 dark:text-white">{c.courseName || 'Course'}</td>
                                    <td className="px-4 sm:px-8 py-4 sm:py-5 text-sm font-black text-emerald-500">Rp {(c.amount || 0).toLocaleString('id-ID')}</td>
                                    <td className="px-4 sm:px-8 py-4 sm:py-5">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold tracking-widest uppercase ${getStatusBadgeColor(c.status)}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="px-4 sm:px-8 py-4 sm:py-5 text-sm font-bold text-slate-500 dark:text-slate-400">{c.createdAt ? new Date(c.createdAt).toLocaleDateString('id-ID') : '-'}</td>
                                </tr>
                            )) || (
                                    <tr><td colSpan={4} className="px-8 py-16 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">Belum ada riwayat komisi</td></tr>
                                )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Withdrawal History */}
            <div className="rounded-[2.5rem] bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
                <div className="px-8 py-6 border-b border-slate-50 dark:border-white/5 flex items-center gap-4">
                    <div className="size-10 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[24px]">payments</span>
                    </div>
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Riwayat Penarikan</h3>
                </div>
                <div className="divide-y divide-slate-50 dark:divide-white/5">
                    {withdrawals?.slice(0, 5).map((w: any) => (
                        <div key={w.id} className="flex items-center justify-between px-8 py-6 hover:bg-slate-50/50 dark:hover:bg-white/[0.01] transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="size-12 rounded-2xl bg-slate-50 dark:bg-white/5 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-slate-400">receipt_long</span>
                                </div>
                                <div>
                                    <p className="text-base font-black text-slate-900 dark:text-white">Rp {(w.amount || 0).toLocaleString('id-ID')}</p>
                                    <p className="text-xs font-bold text-slate-400 tracking-wider mt-0.5">{w.createdAt ? new Date(w.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                                </div>
                            </div>
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold tracking-widest uppercase ${getStatusBadgeColor(w.status)}`}>
                                {w.status}
                            </span>
                        </div>
                    )) || (
                            <div className="px-8 py-16 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">Belum ada riwayat penarikan</div>
                        )}
                </div>
            </div>

            {/* Withdrawal Modal */}
            {showWithdrawModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={resetWithdrawModal}>
                    <div className="bg-gray-50 dark:bg-surface-dark rounded-lg p-6 w-full max-w-md border border-gray-200 dark:border-white/10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Request Withdrawal</h2>
                            <button onClick={resetWithdrawModal} className="size-8 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 flex items-center justify-center transition-colors">
                                <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
                            </button>
                        </div>
                        <div className="mb-4 p-4 rounded-lg bg-primary/10 border border-primary/20">
                            <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Available Balance</p>
                            <p className="text-2xl font-bold text-red-400">Rp {availableBalance.toLocaleString('id-ID')}</p>
                            <p className="text-xs text-gray-500 dark:text-text-muted mt-2">Minimum withdrawal: Rp {minWithdrawal.toLocaleString('id-ID')}</p>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Amount (IDR)</label>
                                <input
                                    type="number"
                                    value={withdrawAmount}
                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                    placeholder="Enter amount"
                                    min={minWithdrawal}
                                    max={availableBalance}
                                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Bank Name</label>
                                <select
                                    value={withdrawBankName}
                                    onChange={(e) => setWithdrawBankName(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
                                >
                                    <option value="" className="bg-gray-50 dark:bg-surface-dark">Select bank</option>
                                    <option value="BCA" className="bg-gray-50 dark:bg-surface-dark">BCA</option>
                                    <option value="Mandiri" className="bg-gray-50 dark:bg-surface-dark">Mandiri</option>
                                    <option value="BNI" className="bg-gray-50 dark:bg-surface-dark">BNI</option>
                                    <option value="BRI" className="bg-gray-50 dark:bg-surface-dark">BRI</option>
                                    <option value="CIMB" className="bg-gray-50 dark:bg-surface-dark">CIMB Niaga</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Account Number</label>
                                <input
                                    type="text"
                                    value={withdrawAccountNumber}
                                    onChange={(e) => setWithdrawAccountNumber(e.target.value)}
                                    placeholder="Enter account number"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-text-muted mb-2">Account Holder Name</label>
                                <input
                                    type="text"
                                    value={withdrawAccountName}
                                    onChange={(e) => setWithdrawAccountName(e.target.value)}
                                    placeholder="Name as shown on bank account"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-primary/50"
                                />
                            </div>
                        </div>
                        {withdrawError && (
                            <p className="text-xs text-red-400 text-center mt-4">{withdrawError}</p>
                        )}
                        <div className="flex gap-3 mt-6">
                            <button onClick={resetWithdrawModal} className="flex-1 py-2.5 rounded-lg bg-gray-100 dark:bg-white/5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleWithdrawSubmit}
                                disabled={availableBalance < minWithdrawal || requestWithdrawal.isPending}
                                className="flex-1 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {requestWithdrawal.isPending ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default InstructorEarningsPage
